import { Badge } from '@/components/ui';
import { PaymentPlatformEnum } from '@/enums';
import { CreditCard, Wallet, Smartphone, Banknote, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type ProviderConfig = {
	icon: LucideIcon;
	className: string;
	label: string;
};

const providerConfigs: Record<PaymentPlatformEnum, ProviderConfig> = {
	[PaymentPlatformEnum.STRIPE]: {
		icon: CreditCard,
		className:
			'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800',
		label: 'Stripe',
	},
	[PaymentPlatformEnum.PAYPAL]: {
		icon: Wallet,
		className:
			'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
		label: 'PayPal',
	},
	[PaymentPlatformEnum.APPLE_IAP]: {
		icon: Smartphone,
		className:
			'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
		label: 'Apple IAP',
	},
	[PaymentPlatformEnum.GOOGLE_IAP]: {
		icon: Smartphone,
		className:
			'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
		label: 'Google IAP',
	},
	[PaymentPlatformEnum.PAWAPAY]: {
		icon: Banknote,
		className:
			'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
		label: 'Pawapay',
	},
	[PaymentPlatformEnum.MYCOOLPAY]: {
		icon: CreditCard,
		className:
			'bg-gray-50 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600',
		label: 'MyCoolPay',
	},
	[PaymentPlatformEnum.CAMPAY]: {
		icon: CreditCard,
		className:
			'bg-gray-50 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600',
		label: 'CamPay',
	},
	[PaymentPlatformEnum.FEDAPAY]: {
		icon: Banknote,
		className:
			'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800',
		label: 'FedaPay',
	},
	[PaymentPlatformEnum.HUB2]: {
		icon: Banknote,
		className:
			'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800',
		label: 'Hub2',
	},
	[PaymentPlatformEnum.WAVE]: {
		icon: Banknote,
		className:
			'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-800',
		label: 'Wave',
	},
	[PaymentPlatformEnum.FEEXPAY]: {
		icon: Banknote,
		className:
			'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800',
		label: 'FeexPay',
	},
	[PaymentPlatformEnum.NEXUS]: {
		icon: Banknote,
		className:
			'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800',
		label: 'Nexus',
	},
};

interface ProviderBadgeProps {
	provider: PaymentPlatformEnum;
	className?: string;
}

/**
 * Displays a styled badge for payment providers.
 * Each provider has a distinct color scheme and icon.
 */
const ProviderBadge = ({ provider, className }: ProviderBadgeProps) => {
	const config = providerConfigs[provider] ?? {
		icon: CreditCard,
		className:
			'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
		label: provider ?? 'Unknown',
	};
	const Icon = config.icon;

	return (
		<Badge
			variant="outline"
			className={cn(config.className, 'transition-colors duration-200', className)}
		>
			<Icon className="w-3 h-3 mr-1" />
			{config.label}
		</Badge>
	);
};

export default ProviderBadge;
